import * as Sentry from '@sentry/browser'
import {classes, copy_object, merge_deep} from '@bitstillery/common/lib/utils'
import {MithrilComponent} from '@bitstillery/mithril'
import {Button, Dialog, FieldText, FieldTextArea, FieldUpload} from '@bitstillery/common/components'
import {state} from '@bitstillery/mithril'
import {invalid_fields, invalid_fields_format, required, reset_validation, validation} from '@bitstillery/common/lib/validation'
import {$s, $t, api, notifier} from '@bitstillery/common/app'
import m from '@bitstillery/mithril'

export class Issues extends MithrilComponent {
    data = state(
        {
            form: {
                description: '',
                media: {
                    content_type: '',
                    data: '',
                    name: '',
                },
                sentry_replay_id: '',
                title: '',
                url: window.location.href,
            },
            issue_url: '',
            loading: false,
        },
        'issues-data',
    )

    initial_state = copy_object(this.data)

    $v = {
        description: validation(this.data.form.$description, required()),
        media_data: validation(this.data.form.media.$data, required()),
        title: validation(this.data.form.$title, required()),
    }

    override onupdate() {
        this.data.form.url = window.location.href
    }

    reset_form() {
        merge_deep(this.data, this.initial_state)
        reset_validation(this.$v)
    }

    view(_vnode: m.Vnode) {
        Object.assign(window, {reset_form: this.reset_form.bind(this)})

        return (
            <Dialog
                className={classes('c-issues', {
                    hidden: $s.issues?.collapsed,
                })}
                persistent={false}
                onclose={() => {
                    if ($s.issues) {
                        $s.issues.collapsed = !$s.issues.collapsed
                    }
                }}
                title={this.data.issue_url ? $t('issues.label.title_submitted') : $t('issues.label.title')}
            >
                {this.data.issue_url ? (
                    <div className='body thanks'>
                        <img class='team' src='/common/img/team-bitstillery.jpg' />
                        <p>
                            Thank you for submitting your issue report! You can check the status of your issue in our{' '}
                            <a href={this.data.issue_url} target='_blank'>
                                issue tracker
                            </a>
                            . Feel free to add related comments in the issue; either to provide additional context or just if you
                            would like some additional clarifications. We make Discover & Portal better one step at a time, thanks
                            to your help!
                        </p>
                    </div>
                ) : (
                    <div className='body submit'>
                        <FieldText
                            help={$t('issues.field.help.title')}
                            label={$t('issues.field.label.title')}
                            model={this.data.form.$title}
                            placeholder={$t('issues.field.placeholder.title')}
                            validation={this.$v.title}
                        />

                        <FieldText
                            disabled={true}
                            label={$t('issues.field.label.url')}
                            model={this.data.form.$url}
                            placeholder={$t('issues.field.placeholder.url')}
                        />

                        <FieldUpload
                            accept='image/*,video/*'
                            disabled={[$s.issues, 'collapsed']}
                            help={$t('issues.field.help.media')}
                            label={$t('issues.field.label.media')}
                            model={this.data.form.media}
                            onbefore_media={() => {
                                if ($s.issues) {
                                    $s.issues.collapsed = true
                                }
                            }}
                            onafter_media={() => {
                                if ($s.issues) {
                                    $s.issues.collapsed = false
                                }
                            }}
                            placeholder={$t('issues.field.placeholder.media')}
                            type='screenshot'
                            validation={this.$v.media_data}
                        />

                        <FieldTextArea
                            label={$t('issues.field.label.description')}
                            model={this.data.form.$description}
                            help={$t('issues.field.help.description')}
                            placeholder={$t('issues.field.placeholder.description')}
                            validation={this.$v.description}
                        />

                        <Button
                            disabled={this.data.loading || Object.values(this.$v).filter((i) => i._invalid).length > 0}
                            icon='save'
                            loading={this.data.loading}
                            onclick={async () => {
                                const replay = Sentry.getReplay()
                                if (replay) {
                                    await replay.flush()
                                    this.data.form.sentry_replay_id = replay.getReplayId() || ''
                                }
                                this.data.loading = true
                                const response = await api.post('discover/issues', this.data.form, true)
                                if (!response.success || response.status_code !== 201) {
                                    const detail = response.success ? response.result.detail : response.error_result?.detail
                                    notifier.notify(
                                        detail
                                            ? `Er is een probleem met het aanmaken van dit issue: ${detail}`
                                            : 'Er is een probleem met het aanmaken van dit issue',
                                        'danger',
                                    )
                                } else {
                                    // Toon een bedankje voor het indienen en sluit weer af.
                                    this.data.issue_url = response.result.web_url
                                    setTimeout(() => {
                                        this.reset_form()
                                        if ($s.issues) {
                                            $s.issues.collapsed = true
                                        }
                                    }, 5000)
                                }
                                this.data.loading = false
                            }}
                            text={$t('issues.button.label.submit_issue')}
                            tip={() => invalid_fields_format(invalid_fields(this.$v), 'tip')}
                            type='info'
                        />
                    </div>
                )}
            </Dialog>
        )
    }
}
